import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff, Wifi, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { offlineQueueService } from '../services/offlineQueueService';

export const OfflineIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [queueSize, setQueueSize] = useState(offlineQueueService.getQueueSize());
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      // Hide reconnected message after 3 seconds
      setTimeout(() => setShowReconnected(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to queue changes
  useEffect(() => {
    const unsubscribe = offlineQueueService.onQueueChange((queue) => {
      setQueueSize(queue.length);
      setIsSyncing(offlineQueueService.isProcessing());
    });

    return unsubscribe;
  }, []);

  const handleRetry = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);
    const successCount = await offlineQueueService.processQueue();
    setIsSyncing(false);

    if (successCount > 0) {
      toast.success(
        `Synced ${successCount} ${successCount === 1 ? 'action' : 'actions'}`,
        { duration: 3000 }
      );
    }
  }, [isSyncing, isOnline]);

  // Don't render anything if online, not showing reconnected message, and no queue
  if (isOnline && !showReconnected && queueSize === 0) return null;

  // Show the queue badge on the offline bar
  const showQueueBadge = queueSize > 0;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        isOnline
          ? 'bg-green-500 dark:bg-green-600'
          : 'bg-red-500 dark:bg-red-600'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center justify-center gap-2 py-2 px-4 text-white text-sm font-medium">
        {isOnline ? (
          <>
            <Wifi className="w-4 h-4" aria-hidden="true" />
            <span>Back online</span>
            {showQueueBadge && (
              <>
                <span className="mx-1 opacity-60" aria-hidden="true">|</span>
                <span className="text-xs opacity-90">
                  {queueSize} pending {queueSize === 1 ? 'action' : 'actions'}
                </span>
                <button
                  onClick={handleRetry}
                  disabled={isSyncing}
                  className="ml-1 p-1 rounded-md bg-white/20 hover:bg-white/30 disabled:opacity-50
                             disabled:cursor-not-allowed transition-colors"
                  aria-label="Retry syncing queued actions"
                >
                  {isSyncing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" aria-hidden="true" />
            <span>No internet connection</span>
            {showQueueBadge && (
              <>
                <span className="mx-1 opacity-60" aria-hidden="true">|</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
                                 text-xs font-bold bg-white/25 rounded-full">
                  {queueSize}
                </span>
                <span className="text-xs opacity-90">queued</span>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
